import dayjs from "dayjs";

// Project import
import getFileIcon from "features/files/components/getFileIcon";

import { UploadFileEntryProps, FileRootFolders, FirestoreFileProps } from "types/files";
import { ListItemProps } from "features/files/components/filesMainCard";
import { MapFeatureFilesConfigProps } from "types/appFunctionality";


export function getRootFolderAndRootIdFromUploadFileProps(data: UploadFileEntryProps): { rootFolder: FileRootFolders, rootId: string } | null {
   const { orgId, estateId, mapId, projectId, activityId } = data;
   if (activityId) return { rootFolder: "activities", rootId: activityId };
   if (projectId) return { rootFolder: "projects", rootId: projectId };
   if (mapId) return { rootFolder: "maps", rootId: mapId };
   if (estateId) return { rootFolder: "estates", rootId: estateId };
   if (orgId) return { rootFolder: "orgs", rootId: orgId };
   return null;
}

export function filePickerErrorInterpreter(errors: any[], translate: (key: string) => string): string | void {
   if (errors.length > 0) {
      switch (errors[0].name) {
         case "FileAmountLimitError":
            return translate("file-upload-error-amount");
         case "FileTypeError":
            return translate("file-upload-error-type");
         case "FileSizeError":
            return translate("file-upload-error-size");
         default:
            return translate("file-upload-error-generic");
      }
   }
}

type MapFromFileEntryPropsToFileListItemProps = {
   file: FirestoreFileProps,
   rootFolder?: string, 
   config: MapFeatureFilesConfigProps,
   maps?: { [key: string]: any },
   projects?: { [key: string]: any },
   translate: (key: string) => string,
   onChange?: (file: FirestoreFileProps) => void,
   onDownload?: (file: FirestoreFileProps) => void,
   onDelete?: (file: FirestoreFileProps) => void,
};

export function mapFromFileEntryPropsToFileListItem({ file, rootFolder, config, maps, projects, translate, onChange, onDelete, onDownload }: MapFromFileEntryPropsToFileListItemProps): ListItemProps[] {
   const { id, estateId, categories, mapId, projectId, featureRefLabels, name, type, size, creationDate, lastModifiedDate, url, thumbnailUrl, readBy, uploadedBy, exifData } = file;
   // Set data for list item
   // Set icon, thumbnail, and url
   let icon = undefined;
   let thumbnail = thumbnailUrl ? thumbnailUrl : undefined;
   let imgPath = undefined;
   if (!thumbnail) {
      icon = getFileIcon(type);
   } else {
      imgPath = url;
   }
   
   // Set association type (map, project)
   let associationType = "";
   let associationName = "";
   if (mapId && maps) {
      associationType = "map";
      const map = maps[mapId];
      if (map) associationName = map.name ? map.name : "";
   } else if (projectId && projects) {
      associationType = "project";
      const project = projects[projectId];
      if (project) associationName = project.name ? project.name : "";
   } 
   // Set linkage
   let linkageLabel = "";
   let linkageTitle = (associationType === "project" || (rootFolder && rootFolder === "project")) ? translate("linked-activity") : translate("linked");
   if (featureRefLabels) {
      linkageLabel = featureRefLabels.map(el => el.label).join(", ");
   } else if (exifData && exifData.location && exifData.location.lat !== 0 && exifData.location.lng !== 0) {
      const { lat, lng } = exifData.location as { lat: number, lng: number };
      linkageLabel = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      linkageTitle = translate("location");
   }
   // Set uploaded by
   let uploadedByName = "";
   if (uploadedBy) {
      const { name, email } = uploadedBy;
      uploadedByName = name ? name : (email ? email : "");
   }
   // Set dot menu items
   let dotMenuEntry = null;
   let dotMenuItems = [];
   if (onChange) dotMenuItems.push({ disabled: config.change !== "enabled", type: "default" as "default", label: translate("replace-file"), onClick: () => onChange(file) });
   if (onDownload) dotMenuItems.push({ disabled: config.download !== "enabled", type: "default" as "default", label: translate("download-file"), onClick: () => onDownload(file) });
   if (onDelete) dotMenuItems.push({ disabled: config.delete !== "enabled",type: "default" as "default", label: translate("delete"), onClick: () => onDelete(file) });
   if (dotMenuItems.length > 0) {
      dotMenuEntry = {
         label: translate('dot-menu-tooltip'),
         action: 'row-menu',
         menuItems: dotMenuItems
      };
   };

   // Create list row with items
   const listItemProps: ListItemProps[] = [
      {
         label: translate("creation-date-text"),
         data: dayjs(lastModifiedDate ? lastModifiedDate : creationDate).format('DD.MM.YYYY - HH:mm'),
         icon: icon,
         fileUrl: url,
         thumbnailPath: thumbnail,
         imgPath: imgPath,
         display: true,
      },
      {
         label: translate("file-name"),
         data: name,
         display: true,
      },
      {
         label: associationType !== "" ? translate(associationType) : "",
         data: associationName,
         display: associationType !== "" ? true : false,
      },
      {
         label: linkageTitle,
         data: linkageLabel,
         display: true,
      },
      {
         label: translate("file-uploaded-by"),
         data: uploadedByName,
         display: true,
      }
   ];
   if (categories && categories.length > 0) listItemProps.push({ folder: categories[0], display: false });
   listItemProps.push({
      meta: {
         id,
         estateId, mapId, projectId,
         size: size,
         lastModifiedDate,
         readBy
      }, display: false
   });
   if (dotMenuEntry) listItemProps.push(dotMenuEntry);

   return listItemProps;
}


type MapFromFileToFileListItemProps = {
   file: File,
   index: number,
   config: MapFeatureFilesConfigProps,
   translate: (key: string) => string,
   onChange?: (file: File) => void,
   onDownload?: (file: File) => void,
   onDelete?: (index: number) => void,
};

export function mapFromFileToFileListItem({ file, index, config, translate, onChange, onDownload, onDelete }: MapFromFileToFileListItemProps): ListItemProps[] {
   const { name, type } = file;

   // Set dot menu items
   let dotMenuEntry = null;
   let dotMenuItems = [];
   if (onChange) dotMenuItems.push({ disabled: config.change !== "enabled", type: "default" as "default", label: translate("replace-file"), onClick: () => onChange(file) });
   if (onDownload) dotMenuItems.push({ disabled: config.download !== "enabled", type: "default" as "default", label: translate("download-file"), onClick: () => onDownload(file) });
   if (onDelete) dotMenuItems.push({ disabled: config.delete !== "enabled",type: "default" as "default", label: translate("delete"), onClick: () => onDelete(index) });
   if (dotMenuItems.length > 0) {
      dotMenuEntry = {
         label: translate('dot-menu-tooltip'),
         action: 'row-menu',
         menuItems: dotMenuItems
      };
   };

   // Create list row
   const listItemProps: ListItemProps[] = [
      {
         label: translate("creation-date-text"),
         data: dayjs().format('DD.MM.YYYY - HH:mm'),
         icon: getFileIcon(type),
         display: true,
      },
      {
         label: translate("file-name"),
         data: name,
         display: true,
      },
   ];
   if (dotMenuEntry) listItemProps.push(dotMenuEntry);

   return listItemProps;
}